import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { Toggle } from '../../../components/ui/Toggle';

const PrivacySection = ({ privacySettings, onUpdatePrivacy, onDeleteAccount }) => {
  const [settings, setSettings] = useState({
    defaultTripPrivacy: privacySettings?.defaultTripPrivacy || 'private'
  });

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // ✅ CRITICAL FIX: Sync local state when props change
  useEffect(() => {
    if (privacySettings) {
      setSettings({
        defaultTripPrivacy: privacySettings?.defaultTripPrivacy || 'private'
      });
    }
  }, [privacySettings]);

  // ✅ FIXED: Extract checked value from event object
  const handleToggleChange = async (e) => {
    const isChecked = e?.target?.checked ?? e;
    const newPrivacy = isChecked ? 'public' : 'private';
    
    setSettings(prev => ({
      ...prev,
      defaultTripPrivacy: newPrivacy
    }));

    setIsSaving(true);
    setSaveMessage('');

    try {
      await onUpdatePrivacy({ defaultTripPrivacy: newPrivacy });
      setSaveMessage('Privacy preference saved!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to update privacy:', error);
      // Revert on error
      setSettings(prev => ({
        ...prev,
        defaultTripPrivacy: privacySettings?.defaultTripPrivacy || 'private'
      }));
      setSaveMessage('Failed to save. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdatePrivacy(settings);
    } catch (error) {
      console.error('Failed to update privacy settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteAccount();
    } catch (error) {
      console.error('Failed to delete account:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {saveMessage && (
        <div className={`p-3 rounded-organic ${saveMessage?.includes('Failed') ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}`}>
          <div className="flex items-center space-x-2">
            <Icon name={saveMessage?.includes('Failed') ? 'AlertCircle' : 'CheckCircle'} size={16} />
            <span className="text-sm">{saveMessage}</span>
          </div>
        </div>
      )}
      {/* Default Trip Privacy Section */}
      <div className="bg-card rounded-organic border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
            <Icon name="MapPin" size={20} color="white" />
          </div>
          <div>
            <h3 className="font-heading font-heading-bold text-lg text-card-foreground">Default Trip Privacy</h3>
            <p className="font-caption text-sm text-muted-foreground">Set the default privacy level for new trips</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* ✅ REPLACED: Select dropdown with Toggle component */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-organic">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <Icon 
                  name={settings?.defaultTripPrivacy === 'public' ? 'Globe' : 'Lock'} 
                  size={18} 
                  className="text-primary"
                />
                <label className="font-body font-body-medium text-sm text-card-foreground">
                  {settings?.defaultTripPrivacy === 'public' ? 'Public' : 'Private'}
                </label>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                {settings?.defaultTripPrivacy === 'public' ?'New trips will be visible to anyone by default' :'New trips will only be visible to you by default'
                }
              </p>
            </div>
            
            <Toggle
              checked={settings?.defaultTripPrivacy === 'public'}
              onChange={handleToggleChange}
              disabled={isSaving}
              size="md"
            />
          </div>

          <div className="p-4 bg-muted/30 rounded-organic">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-body font-body-medium text-sm text-card-foreground mb-1">About Trip Privacy</h4>
                <ul className="font-caption text-xs text-muted-foreground space-y-1">
                  <li>• <strong>Public:</strong> New trips will be visible to anyone with the link</li>
                  <li>• <strong>Private:</strong> New trips will only be visible to you</li>
                  <li>• You can always change the privacy setting for individual trips later</li>
                  <li>• This only affects newly created trips, not existing ones</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Account Deletion */}
      <div className="bg-card rounded-organic border border-error/20 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-error rounded-full flex items-center justify-center">
            <Icon name="Trash2" size={20} color="white" />
          </div>
          <div>
            <h3 className="font-heading font-heading-bold text-lg text-error">Delete Account</h3>
            <p className="font-caption text-sm text-muted-foreground">Permanently delete your account and all data</p>
          </div>
        </div>

        {!showDeleteConfirmation ? (
          <div className="flex items-center justify-between p-4 border border-error/20 rounded-organic-sm bg-error/5">
            <div>
              <h4 className="font-body font-body-medium text-sm text-card-foreground">Permanent Account Deletion</h4>
              <p className="font-caption text-xs text-muted-foreground mt-1">
                This action cannot be undone. All your trips, preferences, and data will be permanently deleted.
              </p>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              Delete Account
            </Button>
          </div>
        ) : (
          <div className="space-y-4 p-4 border border-error/20 rounded-organic-sm bg-error/5">
            <div className="space-y-2">
              <h4 className="font-body font-body-medium text-sm text-error">Confirm Account Deletion</h4>
              <p className="font-caption text-xs text-muted-foreground">
                Type <strong>DELETE</strong> in the box below to confirm you want to permanently delete your account.
              </p>
            </div>
            
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e?.target?.value)}
              placeholder="Type DELETE to confirm"
              className="w-full px-3 py-2 border border-border rounded-organic-sm text-sm focus:outline-none focus:ring-2 focus:ring-error"
            />
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAccount}
                loading={isDeleting}
                disabled={deleteConfirmText !== 'DELETE'}
              >
                Delete Account
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacySection;